import { atom } from 'infrastructure/state/jotai.wrapper'
import { atomWithRefresh, loadable } from 'jotai/utils'
import { getAPIRequest } from 'infrastructure/api'
import { deepMapKeysToCamel } from 'shared/utils/object'
import {
  IMasterCurricula,
  GroupType,
  PartnerType,
  PrimaryTreeType,
  ProductType,
} from 'infrastructure/api'
import { getSortedByValue } from 'shared/utils/sorting'
import { doAdminQueries, IQueryInfo } from 'infrastructure/api'
import { sharedQueryFields } from 'shared/api/utility'
import { objectFieldSort } from 'shared/utils'
import {
  useAtomWithErrorNotification,
  WithErrorResult,
} from 'shared/ui/GlobalDialog'

interface ISMasterCurricula {
  created_at: string
  name: string
  title: string
  tree_id: string
  updated_at: string
  version: string
}

const mastersResponseAtom = loadable(
  atom(async () => {
    return getAPIRequest<ISMasterCurricula[]>('/api/tree/masters').then(
      (resp) => {
        return resp.map((m) => deepMapKeysToCamel<IMasterCurricula>(m))
      },
    )
  }),
)

export const mastersByIdWithErrorAtom = atom<
  WithErrorResult<Record<string, IMasterCurricula[]>>
>((get) => {
  const resp = get(mastersResponseAtom)
  if (resp.state === 'hasData') {
    const result = getSortedByValue<IMasterCurricula>(resp.data, [
      'treeId',
      'createdAt',
    ])
    return {
      result,
      error: undefined,
    }
  }
  if (resp.state === 'hasError') {
    return {
      result: undefined,
      error: String(resp.error),
    }
  }
  return {
    result: undefined,
    error: undefined,
  }
})

export function useMastersByIdWithErrorNotification() {
  return useAtomWithErrorNotification<Record<string, IMasterCurricula[]>>(
    { errorTitle: 'Error Retrieving Masters' },
    mastersByIdWithErrorAtom,
  )
}

export const allProducts = atomWithRefresh(async () => {
  const query: IQueryInfo = {
    resourceData: { key: 'allProducts', fields: sharedQueryFields.product },
    params: {},
  }
  const { data } = await doAdminQueries<ProductType[]>([query])
  return data?.allProducts
})

export const allProductsById = atom(async (get) => {
  const products = await get(allProducts)
  return products?.reduce<Record<number, ProductType>>((acc, product) => {
    return {
      ...acc,
      [product.id]: product,
    }
  }, {})
})

export const allProductsByPrimaryTreeId = atom(async (get) => {
  const products = await get(allProducts)
  return products?.reduce<Record<string, ProductType>>((acc, product) => {
    if (!product.primary_tree_tree_id) return acc
    return {
      ...acc,
      [product.primary_tree_tree_id]: product,
    }
  }, {})
})

export const allPartners = atomWithRefresh(async () => {
  const query: IQueryInfo = {
    resourceData: { key: 'allPartners', fields: sharedQueryFields.partner },
    params: {},
  }
  const { data } = await doAdminQueries<PartnerType[]>([query])
  return data?.allPartners
})

export const allPartnersByName = atom(async (get) => {
  const partners = await get(allPartners)
  if (!partners) return {}
  return partners.reduce<Record<string, PartnerType>>((acc, partner) => {
    return {
      ...acc,
      [partner.name]: partner,
    }
  }, {})
})

export const allProductsWithPrimaryTreeAtom = atomWithRefresh(async () => {
  const fields = [
    ...sharedQueryFields.product,
    { primary_tree: sharedQueryFields.primaryTrees },
  ]
  const query: IQueryInfo = {
    resourceData: { key: 'allProducts', fields },
    params: {},
  }
  const { data } = await doAdminQueries<ProductType[]>([query])
  return data?.allProducts
})

export const allProductsWithPrimaryTreeByIdAtom = atom(async (get) => {
  const products = await get(allProductsWithPrimaryTreeAtom)
  return products?.reduce<Record<string, ProductType>>((acc, p) => {
    return {
      ...acc,
      [p.id]: p,
    }
  }, {})
})

export const allProductsWithPrimaryTreeByTreeIdAtom = atom(async (get) => {
  const products = await get(allProductsWithPrimaryTreeAtom)
  return products?.reduce<Record<string, ProductType>>(
    (acc, p, currentIndex) => {
      return {
        ...acc,
        [p.primary_tree_tree_id ?? currentIndex]: p,
      }
    },
    {},
  )
})

export const allProductGroupsAtom = atomWithRefresh(async () => {
  const query: IQueryInfo = {
    resourceData: { key: 'allGroups', fields: sharedQueryFields.productGroups },
    params: {},
  }
  const { data } = await doAdminQueries<GroupType[]>([query])
  return data?.allGroups
})

export const groupsByIdAtom = atom(async (get) => {
  const groups = await get(allProductGroupsAtom)
  return groups?.reduce<Record<number, GroupType>>((acc, g) => {
    return {
      ...acc,
      [g.id]: g,
    }
  }, {})
})

export const groupsByProductId = atom(async (get) => {
  const groups = await get(allProductGroupsAtom)
  if (!groups) return {}
  return groups.reduce<Record<number, GroupType>>((acc, group) => {
    return {
      ...acc,
      ...group.products.reduce<Record<number, GroupType>>((acc, { id }) => {
        return {
          ...acc,
          [id]: { ...group, products: [] },
        }
      }, {}),
    }
  }, {})
})

export const allPrimaryTrees = atom(async () => {
  const query: IQueryInfo = {
    resourceData: {
      key: 'allPrimaryTrees',
      fields: sharedQueryFields.primaryTrees,
    },
    params: {},
  }
  const { data } = await doAdminQueries<PrimaryTreeType[]>([query])
  return data?.allPrimaryTrees
})

export const allPrimaryTreesByTreeId = atom(async (get) => {
  const primaryTrees = await get(allPrimaryTrees)
  return primaryTrees?.reduce<Record<string, PrimaryTreeType>>((acc, p) => {
    return {
      ...acc,
      [p.tree_id]: p,
    }
  }, {})
})

function indexFieldSort<TObj, TProp extends keyof TObj>(
  field: TProp,
  indexedList: Record<string | number, TObj>,
) {
  return (indexA: string | number, indexB: string | number) => {
    const aVal = indexedList[indexA]?.[field]
    const bVal = indexedList[indexB]?.[field]
    if (!aVal && !bVal) return 0
    if (!aVal) return 1
    if (!bVal) return -1
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal)
    }
    return 0
  }
}

export const productTocAtom = atom(async (get) => {
  const products = (await get(allProducts)) ?? []
  const groups = (await get(allProductGroupsAtom)) ?? []
  const groupsByProdId = await get(groupsByProductId)
  const prodsById = (await get(allProductsById)) ?? {}
  const grouped = products.reduce<Record<number, number[]>>((acc, product) => {
    const { id } = groupsByProdId[product.id] ?? { id: -1 }
    if (!(id in acc)) {
      acc[id] = []
    }
    acc[id].push(product.id)
    return acc
  }, {})
  const titleSorter = indexFieldSort('title', prodsById)
  Object.values(grouped).forEach((prodList) => {
    prodList.sort(titleSorter)
  })
  const groupKeys = groups
    .filter(({ id }) => id in grouped)
    .sort(objectFieldSort('name'))
    .map(({ id }) => id)
  if (grouped[-1]?.length) {
    groupKeys.push(-1)
  }
  return {
    groupKeys,
    grouped,
  }
})
